import type { Recipe } from '../data/recipes';

/**
 * Client-side recipe search over names, tags, and ingredients. This stands in
 * for the future search API; keep the ranking logic here so it can move
 * server-side without touching the UI.
 *
 * Ranking: title match > tag match > ingredient match. Multi-word queries
 * require every word to match somewhere (AND semantics).
 */
export function searchRecipes(recipes: readonly Recipe[], rawQuery: string): Recipe[] {
  const words = rawQuery
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean);

  if (words.length === 0) return [...recipes];

  const scored: { recipe: Recipe; score: number }[] = [];

  for (const recipe of recipes) {
    const title = recipe.title.toLowerCase();
    const tagline = recipe.tagline.toLowerCase();
    const tags = recipe.tags.map((t) => t.toLowerCase());
    const ingredients = recipe.ingredients.map((i) => i.item.toLowerCase());

    let score = 0;
    let allWordsMatch = true;

    for (const word of words) {
      let wordScore = 0;
      if (title.includes(word)) wordScore = 3;
      else if (tags.some((t) => t.includes(word))) wordScore = 2;
      else if (ingredients.some((i) => i.includes(word))) wordScore = 2;
      else if (tagline.includes(word)) wordScore = 1;

      if (wordScore === 0) {
        allWordsMatch = false;
        break;
      }
      score += wordScore;
    }

    if (allWordsMatch) scored.push({ recipe, score });
  }

  return scored.sort((a, b) => b.score - a.score).map((s) => s.recipe);
}
